import { CSSResult } from "lit";
import { Node, Schema } from "prosemirror-model";
import { Plugin, PluginKey } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { exampleSetup } from "prosemirror-example-setup";
import {
    MarkdownSerializerState,
    defaultMarkdownSerializer,
    defaultMarkdownParser,
} from "prosemirror-markdown";
import { baseSerializerConfig } from "./serializer";

import { parseDocument } from "./parser.js";
import { Schema as baseSchema } from "./schema";
import MarkdownIt from "markdown-it";

import { caretFixPlugin } from "@/wc/text-editor/lib/core/plugins/firefox-caret-fix.js";
import {
    MENU_PLUGIN_KEY,
    MenuItem,
    MenuFunction,
    MenuStateFunction,
    menuPlugin,
} from "@/wc/text-editor/lib/core/menu";
import { popupManagerPlugin } from "@/wc/text-editor/lib/core/popups";
import { defaultKeymaps } from "@/wc/text-editor/lib/core/keymap";
import { BaseExtension } from "../base-extension";
import { ParseSpecMap } from "../types";

/**
 * Node serializer spec describes how a ProseMirror node should be turned into MarkdownIt AST.
 *
 *
 * This spec is defined by prosemirror-markdown and is not documented very well.
 * For some examples of how the standard nodes are implemented, see:
 * https://github.com/ProseMirror/prosemirror-markdown/blob/master/src/to_ts
 *
 * @state
 */
export type NodeSerializerSpec = (
    state: MarkdownSerializerState,
    node: Node,
    parent: Node,
    index: number
) => void;

export type MarkdownSerializerConfig = {
    nodes: { [node_name: string]: NodeSerializerSpec };
    marks: typeof defaultMarkdownSerializer.marks;
    options: typeof defaultMarkdownSerializer.options;
};

export interface EditorConfig {
    schema(): Schema;
    parse: (src: string) => Promise<Node>;
    serializerConfig(): MarkdownSerializerConfig;
    menu: MenuStateFunction;
    plugins: (schema: Schema) => Array<Plugin>;
    parseSpecs: (schema: Schema) => ParseSpecMap;
    markdownIt: () => MarkdownIt;
    css: CSSResult[];
}

export class BaseEditorConfig implements EditorConfig {
    markdownIt() {
        return MarkdownIt("commonmark", {
            html: false,
            linkify: true,
        });
    }

    schema(): Schema {
        return baseSchema;
    }

    async parse(src: string) {
        return parseDocument(
            src,
            this.parseSpecs(this.schema()),
            this.markdownIt(),
            this.schema()
        );
    }

    parseSpecs(_schema: Schema) {
        // The default markdown parser is not parameterized by schema
        // but because it comes from the same module, the parse spec will
        // be compatible
        return defaultMarkdownParser.tokens;
    }

    serializerConfig() {
        return baseSerializerConfig;
    }

    menu(_: EditorView) {
        return [];
    }

    plugins = (extendedSchema: Schema) => {
        return [
            ...exampleSetup({
                schema: extendedSchema,
                menuBar: false,
                history: true,
            }),
            defaultKeymaps(extendedSchema),
            caretFixPlugin(),
            popupManagerPlugin(),
            menuPlugin(),
        ];
    };

    css = [];
}

const applyExtensionSchemaModifications = (
    baseSchema: Schema,
    extensions: BaseExtension[]
) => {
    return extensions.reduce(
        (currentSchema, extension) =>
            extension.modifySchema?.(currentSchema) ?? currentSchema,
        baseSchema
    );
};

const applyExtensionMarkdownItModifications = (
    baseMarkdownIt: MarkdownIt,
    extensions: BaseExtension[]
) => {
    return extensions.reduce((currentMarkdownIt, extension) => {
        return (
            extension.modifyMarkdownIt?.(currentMarkdownIt) ?? currentMarkdownIt
        );
    }, baseMarkdownIt);
};

const applyExtensionParserModifications = (
    baseParseSpec: ParseSpecMap,
    extensions: BaseExtension[],
    extendedSchema: Schema
): ParseSpecMap => {
    return extensions.reduce(
        (currentSpec, extension) =>
            extension.modifyParser?.(currentSpec, extendedSchema) ??
            currentSpec,
        baseParseSpec
    );
};

const applyExtensionSerializerConfigModifications = (
    baseConfig: MarkdownSerializerConfig,
    extensions: BaseExtension[]
): MarkdownSerializerConfig => {
    return extensions.reduce(
        (currentConfig, extension) =>
            extension.modifySerializer?.(currentConfig) ?? currentConfig,
        baseConfig
    );
};

const upgradeDocument = async (
    currentDoc: Node,
    extensions: BaseExtension[],
    extendedSchema: Schema
): Promise<Node> => {
    return await extensions.reduce(
        async (currentDocPromise: Promise<Node>, extension) =>
            extension.upgradeParsedDocument
                ? currentDocPromise.then((doc) =>
                      extension.upgradeParsedDocument!(doc, extendedSchema)
                  )
                : currentDocPromise,
        Promise.resolve(currentDoc)
    );
};

const mergeExtensionPlugins = (
    baseConfig: EditorConfig,
    extensions: BaseExtension[],
    schema: Schema
): Array<Plugin> => {
    const pluginKeys: PluginKey[] = [];
    const plugins = extensions
        .reduce(
            (currentPlugins, extension) => [
                ...(extension?.plugins?.(schema) ?? []),
                ...currentPlugins,
            ],
            baseConfig.plugins(schema)
        )
        .filter((plugin) => {
            // Prevent adding plugin with the same key.
            if (!plugin.spec.key) {
                return true;
            }
            if (pluginKeys.includes(plugin.spec.key)) {
                return false;
            }
            pluginKeys.push(plugin.spec.key);
            return true;
        });
    return plugins;
};

const mergeMenus = (
    baseConfig: EditorConfig,
    extensions: BaseExtension[]
): MenuFunction => {
    return extensions.reduce(
        (currentMenu, extension) =>
            extension.menu?.(currentMenu) ?? currentMenu,
        (view: EditorView) => baseConfig.menu(view) as MenuItem[]
    );
};

export const configureEditor: (config: {
    baseConfig: EditorConfig;
    extensions: BaseExtension[];
}) => EditorConfig = ({ baseConfig, extensions }) => {
    // First set up the schema. This instance of the schema should be used by
    // all parts of the editor.
    const extendedSchema = applyExtensionSchemaModifications(
        baseConfig.schema(),
        extensions
    );

    const parseSpecs = applyExtensionParserModifications(
        baseConfig.parseSpecs(extendedSchema),
        extensions,
        extendedSchema
    );

    const extendedMarkdownIt = applyExtensionMarkdownItModifications(
        baseConfig.markdownIt(),
        extensions
    );

    const extendedSerializerConfig =
        applyExtensionSerializerConfigModifications(
            baseConfig.serializerConfig(),
            extensions
        );

    return {
        baseConfig: baseConfig,
        markdownIt: () => extendedMarkdownIt,
        schema: () => extendedSchema,
        parse: (src: string) => {
            return upgradeDocument(
                parseDocument(
                    src,
                    parseSpecs,
                    extendedMarkdownIt,
                    extendedSchema
                ),
                extensions,
                extendedSchema
            );
        },
        parseSpecs: () => parseSpecs,
        plugins: () =>
            mergeExtensionPlugins(baseConfig, extensions, extendedSchema),
        menu: (view: EditorView) => {
            const pluginState = MENU_PLUGIN_KEY.getState(view.state);
            if (!pluginState) return;
            const items = mergeMenus(baseConfig, extensions)(view);
            pluginState.items = [...pluginState.items, ...items];
            return items;
        },
        serializerConfig: () => extendedSerializerConfig,
        css: extensions.map((e) => e.css).flat(),
    } as EditorConfig;
};
